import React, { useEffect, useId, useRef, useState } from "react";

const HOSTED_BUTTON_ID = "MK8CGFGDUPPGW";

/**
 * Renders the PayPal Hosted Button. The PayPal JS SDK is loaded once in
 * index.html (<head>). This component only calls `paypal.HostedButtons(...)`,
 * each instance into its own unique container so the button can appear in
 * multiple sections of the page without duplicating the SDK.
 */
export const PayPalHostedButton = ({ onLight = false }) => {
  const rawId = useId();
  const containerId = "paypal-container-" + rawId.replace(/[^a-zA-Z0-9]/g, "");
  const rendered = useRef(false);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    function tryRender() {
      if (cancelled || rendered.current) return;
      attempts += 1;
      if (window.paypal && window.paypal.HostedButtons) {
        rendered.current = true;
        try {
          window.paypal
            .HostedButtons({ hostedButtonId: HOSTED_BUTTON_ID })
            .render("#" + containerId);
          setStatus("ready");
        } catch (e) {
          console.error("PayPal hosted button render failed", e);
          setStatus("error");
        }
      } else if (attempts < 40) {
        setTimeout(tryRender, 300);
      } else {
        setStatus("error");
      }
    }
    tryRender();
    return () => {
      cancelled = true;
    };
  }, [containerId]);

  return (
    <div className="min-h-[52px]" data-testid="paypal-hosted-button">
      <div id={containerId} />
      {status === "loading" && (
        <div className={`flex items-center justify-center gap-3 py-4 text-sm ${onLight ? "text-slate-500" : "text-[#9AAEC6]"}`}>
          <span className={`w-4 h-4 rounded-full border-2 animate-spin ${onLight ? "border-slate-200 border-t-slate-500" : "border-white/15 border-t-[#22D3EE]"}`} />
          Loading secure checkout…
        </div>
      )}
      {status === "error" && (
        <div className={`text-center text-sm py-3 ${onLight ? "text-red-600" : "text-[#F87171]"}`} data-testid="paypal-error">
          Checkout couldn't load. Please refresh the page or try again.
        </div>
      )}
    </div>
  );
};
